/**
 * Pending Save utility — preserves "save event" intent across the login flow.
 *
 * Used by Opzione B (hard wall) auth model: when an anonymous user clicks "salva"
 * on an event, we redirect to /accedi after recording the intent here. Once the
 * user completes login, the sync hook (useUserFavoritesSync) reads the pending
 * intent, performs the DB save, clears the pending entry, and navigates back.
 *
 * Storage: localStorage (survives the email confirmation tab flow). TTL 30 min.
 */

const STORAGE_KEY = 'buzzmap_pending_save';
const TTL_MS = 30 * 60 * 1000; // 30 minutes

interface PendingSaveRecord {
  eventId: string;
  returnUrl: string;
  ts: number;
}

/**
 * Validate returnUrl is a safe relative path.
 *
 * Open-redirect defense: a malicious browser extension (or XSS) could overwrite
 * the localStorage entry with an absolute URL like "https://attacker.com/...".
 * If we then navigate() to that value post-login, react-router would happily
 * send the user off-site (or worse, to a phishing clone). We therefore require:
 *   - starts with "/" (relative path)
 *   - does NOT start with "//" (protocol-relative URL — same off-site risk)
 *   - contains no backslashes (some browsers normalise "\\" to "//")
 */
function isSafeReturnUrl(url: string): boolean {
  if (typeof url !== 'string' || url.length === 0) return false;
  if (!url.startsWith('/')) return false;
  if (url.startsWith('//')) return false;
  if (url.includes('\\')) return false;
  return true;
}

/**
 * Persist a pending save intent.
 *
 * Defense in depth: pre-validate returnUrl. If invalid, skip silently (we'd
 * rather lose the pending-save UX than risk an open redirect).
 */
export function setPendingSave(eventId: string, returnUrl: string): void {
  if (typeof window === 'undefined') return;
  if (!isSafeReturnUrl(returnUrl)) return;
  try {
    const record: PendingSaveRecord = {
      eventId,
      returnUrl,
      ts: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore quota errors
  }
}

/**
 * Read the pending save intent if present and not expired.
 * Auto-clears expired/malformed entries. Returns null otherwise.
 */
export function getPendingSave(): { eventId: string; returnUrl: string } | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearPendingSave();
    return null;
  }

  if (!parsed || typeof parsed !== 'object') {
    clearPendingSave();
    return null;
  }
  const o = parsed as Record<string, unknown>;
  if (
    typeof o.eventId !== 'string' ||
    typeof o.returnUrl !== 'string' ||
    typeof o.ts !== 'number'
  ) {
    clearPendingSave();
    return null;
  }

  const age = Date.now() - o.ts;
  if (age < 0 || age > TTL_MS) {
    clearPendingSave();
    return null;
  }

  // Re-validate returnUrl on read: storage could have been tampered with
  // between set and read (extensions, devtools, XSS). Treat invalid as missing.
  if (!isSafeReturnUrl(o.returnUrl)) {
    clearPendingSave();
    return null;
  }

  return { eventId: o.eventId, returnUrl: o.returnUrl };
}

/**
 * Remove any pending save intent.
 */
export function clearPendingSave(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Convenience: record current location as returnUrl, then navigate to /accedi.
 *
 * `navigate` matches the signature of react-router-dom's useNavigate. When the
 * caller can supply a `currentLocation` (Location object from useLocation),
 * we pass it as `backgroundLocation` state so the AuthModal can overlay the
 * current page on desktop (mobile gate inside AppRoutes makes this safe).
 */
type NavigateLike = (path: string, options?: { state?: unknown }) => void;

interface LocationLike {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key: string;
}

export function requestLoginToSave(
  eventId: string,
  navigate: NavigateLike,
  currentLocation?: LocationLike
): void {
  if (typeof window !== 'undefined') {
    const returnUrl = window.location.pathname + window.location.search;
    setPendingSave(eventId, returnUrl);
  }
  if (currentLocation) {
    navigate('/accedi', { state: { backgroundLocation: currentLocation } });
  } else {
    navigate('/accedi');
  }
}
